<template>
  <div class="flex flex-col h-full overflow-hidden">
    <!-- Header -->
    <ThreadsNav show-back :show-center="false" />

    <div class="flex-1 overflow-hidden">
      <VanTabs
        v-model:active="activeTab"
        class="flex flex-col"
        @change="reload"
      >
        <VanTab
          :title="$t('User.mate.driftCapsule.salvageableArea')"
          name="salvageable"
        >
        </VanTab>
        <VanTab :title="$t('User.mate.driftCapsule.salvaged')" name="salvaged">
        </VanTab>
      </VanTabs>

      <ThreadList
        v-show="activeTab === 'salvageable'"
        ref="salvageableScroller"
        :on-pull-load="reload"
        :enable-scroll-load="false"
        class="w-full"
      >
        <div class="h-full" v-if="mateStore.driftCategoryList.length === 0">
          <VanEmpty description="被捞完了，晚点再来看看叭～" />
        </div>
        <div v-else class="grid grid-cols-2 gap-2 px-4 py-2">
          <div
            v-for="category in mateStore.driftCategoryList"
            :key="category.id"
            class="flex flex-col gap-1 justify-center"
            @click="clickCategory(category)"
          >
            <BaseImg
              :media="category.coverMedia"
              alt=""
              class="w-[130px] object-cover"
            />
            <div>{{ category.name }}</div>
            <div class="flex items-center gap-1 text-xs text-base-content/50">
              可打捞次数
              <span>{{ category.availableCapsuleCount }}</span>
              <span>/</span>
              <span>{{ category.totalCapsuleCount }}</span>
            </div>
          </div>
        </div>
      </ThreadList>

      <ThreadList
        v-show="activeTab === 'salvaged'"
        ref="salvagedScroller"
        :on-pull-load="reload"
        :on-scroll-load="loadMateWish"
        class="w-full"
      >
        <Waterfall
          v-if="list.length > 0"
          ref="waterfall"
          :animationDelay="0"
          :breakpoints="{
            768: {
              rowPerView: 3,
            },
            // when wrapper width < 500
            500: {
              rowPerView: 2,
            },
          }"
          :lazyload="false"
          :delay="0"
          :gutter="8"
          :list="list"
          class="bg-base-100"
        >
          <template #item="{ item }">
            <div
              :key="item.prizeMate.uuid"
              class="cursor-pointer rounded-lg overflow-hidden bg-base-200"
              @click="
                router.push({
                  name: 'mateHome',
                  params: {
                    uuid: item.prizeMate.uuid,
                  },
                })
              "
            >
              <div class="relative">
                <BaseImg
                  :url="item.prizeMate.mediaUrl"
                  alt=""
                  crossorigin="anonymous"
                  @loaded="onImgLoaded"
                />
                <div
                  class="absolute left-0 right-0 bottom-0 h-10 flex items-center bg-[rgba(26,26,26,0.5)] px-1.5"
                >
                  <p class="text text-white font-bold text-base line-clamp-1">
                    {{ item.prizeMate.name }}
                  </p>
                  <i
                    v-if="item.gender"
                    :class="[
                      'iconfont ml-0.5 font-bold',
                      item.gender === 1 && 'icon-male text-[#3e83dd]',
                      item.gender === 2 && 'icon-female text-[#EC4899]',
                    ]"
                  />
                </div>
              </div>
              <div class="flex justify-around py-2">
                <button
                  class="btn btn-xs btn-neutral"
                  @click.stop="
                    router.push({
                      name: 'mateEdit',
                      params: {
                        uuid: item.prizeMate.uuid,
                      },
                    })
                  "
                >
                  OC编辑器
                </button>
                <button
                  class="btn btn-xs btn-neutral"
                  @click.stop="
                    router.push({
                      name: 'adoptionCreate',
                      query: {
                        mateId: item.prizeMate.id,
                      },
                    })
                  "
                >
                  +发布领养
                </button>
              </div>
            </div>
          </template>
        </Waterfall>
      </ThreadList>
    </div>

    <DriftCapsuleModal
      v-if="category"
      :category="category"
      @reload="reloadAll"
    />
  </div>
</template>

<script setup lang="ts">
import { readDriftCapsulePage } from "@/apis/mate";
import { ThreadListInstance } from "@/components/mobile/ThreadList.vue";
import { useMate } from "@/stores/mate";
import { PageResponse } from "@/types/api-types";
import { DriftCapsule, DriftCategory } from "@/types/mate";
import { nextTick, onActivated, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Waterfall } from "vue-waterfall-plugin-next";
import "vue-waterfall-plugin-next/dist/style.css";
import DriftCapsuleModal from "./DriftCapsuleModal.vue";

const PER_PAGE = 10;
const waterfall = ref();
const route = useRoute();
const activeTab = ref("salvageable");
const router = useRouter();
const salvageableScroller = ref<ThreadListInstance>();
const salvagedScroller = ref<ThreadListInstance>();
const list = ref<DriftCapsule[]>([]);
const mateStore = useMate();
// let renderTimeout: ReturnType<typeof setTimeout> | null = null; // Variable to hold the timeout ID
// Debounced image loaded handler
const onImgLoaded = () => {
  if (waterfall.value) {
    waterfall.value.renderer(); // Call renderer after 500ms
  }
};

const loadMateWish = async () => {
  const { length } = list.value;
  if (length !== 0 && length % PER_PAGE !== 0) return false;

  const page = length / PER_PAGE + 1;
  let res: PageResponse<DriftCapsule>;
  const baseOption = {
    pagination: { page, perPage: PER_PAGE },
    sort: { field: "dtUpdate", order: "desc" as "desc" | "asc" },
  };
  res = await readDriftCapsulePage({
    ...baseOption,
  });

  list.value = [...list.value, ...res!.page.data];
  nextTick(() => {
    if (waterfall.value) {
      waterfall.value.renderer();
    }
  });
  return res!.page.data.length === PER_PAGE;
};

const category = ref<DriftCategory>();
const reload = async () => {
  if (activeTab.value === "salvageable") {
    mateStore.fetchDriftCategoryList("salvageable");
  } else if (activeTab.value === "salvaged") {
    list.value = [];
    salvagedScroller.value?.resetScrollLoadingState();
  }
};

const clickCategory = (_category: DriftCategory) => {
  category.value = _category;
  mateStore.showDriftCategoryModal = true;
};

const reloadAll = () => {
  mateStore.fetchDriftCategoryList("salvageable");
  list.value = [];
  salvagedScroller.value?.resetScrollLoadingState();
};

const initPage = () => {
  const _categoryId = Number(route.query.categoryId);
  const _tab = route.query.tab;

  if (_categoryId) {
    category.value = mateStore.driftCategoryList.find(
      (category) => category.id === _categoryId
    );
    mateStore.showDriftCategoryModal = true;
  }

  if (_tab === "salvaged") {
    activeTab.value = "salvaged";
  }
};

onMounted(() => {
  initPage();
  mateStore.fetchDriftCategoryList("salvageable");
});

onActivated(() => {
  initPage();
});
</script>

<style>
.waterfall-list {
  background-color: transparent !important;
}
</style>
